import { httpService } from '../../shared/service/httpService';


export interface NotificationDTO {
  id: string;
  company_id: string;
  user_id?: string | null;
  type: number;
  title: string;
  message: string;
  liked: number;
  created_at: string;
  updated_at: string;
}

export interface NotificationBroadcastRequest {
  company_ids: string[];
  user_levels: string[];
  target_all_companies: boolean;
  target_all_user_levels: boolean;
  type: number;
  title: string;
  message: string;
}

export interface NotificationBroadcastResponse {
  company_ids?: string[];
  user_levels?: string[];
  companies_targeted: number;
  users_targeted: number;
  notifications_created: number;
}

const CACHE_TTL_MS = 2500;

const inFlightOpen = new Map<string, Promise<NotificationDTO[]>>();
const inFlightCount = new Map<string, Promise<number>>();

const openCache = new Map<string, { ts: number; data: NotificationDTO[] }>();
const countCache = new Map<string, { ts: number; data: number }>();

const getKey = (companyId: string, userId?: string) => `${companyId}::${userId ?? ''}`;

const isFresh = (ts: number) => Date.now() - ts < CACHE_TTL_MS;

const clearNotificationCaches = () => {
  inFlightOpen.clear();
  inFlightCount.clear();
  openCache.clear();
  countCache.clear();
};

export const notificationService = {
  async getOpen(companyId: string, userId?: string): Promise<NotificationDTO[]> {
    const key = getKey(companyId, userId);
    const cached = openCache.get(key);
    if (cached && isFresh(cached.ts)) return cached.data;

    const pending = inFlightOpen.get(key);
    if (pending) return pending;

    const params: any = { company_id: companyId };
    if (userId) params.user_id = userId;
    const request = httpService
      .get('/api/v1/notifications/open', { params })
      .then((res) => {
        const data = res.data as NotificationDTO[];
        openCache.set(key, { ts: Date.now(), data });
        return data;
      })
      .finally(() => {
        inFlightOpen.delete(key);
      });

    inFlightOpen.set(key, request);
    return request;
  },

  async countOpen(companyId: string, userId?: string): Promise<number> {
    const key = getKey(companyId, userId);
    const cached = countCache.get(key);
    if (cached && isFresh(cached.ts)) return cached.data;

    const pending = inFlightCount.get(key);
    if (pending) return pending;

    const params: any = { company_id: companyId };
    if (userId) params.user_id = userId;
    const request = httpService
      .get('/api/v1/notifications/count', { params })
      .then((res) => {
        const data = res.data as number;
        countCache.set(key, { ts: Date.now(), data });
        return data;
      })
      .finally(() => {
        inFlightCount.delete(key);
      });

    inFlightCount.set(key, request);
    return request;
  },

  async like(id: string): Promise<void> {
    await httpService.post(`/api/v1/notifications/${id}/like`, { liked: 1 });
    clearNotificationCaches();
  },

  async dislike(id: string): Promise<void> {
    await httpService.post(`/api/v1/notifications/${id}/like`, { liked: 0 });
    clearNotificationCaches();
  },

  async likeAll(): Promise<{ ok: boolean; updated: number }> {
    const res = await httpService.post('/api/v1/notifications/like-all', {});
    clearNotificationCaches();
    return res.data as { ok: boolean; updated: number };
  },

  async broadcast(
    payload: NotificationBroadcastRequest
  ): Promise<NotificationBroadcastResponse> {
    const res = await httpService.post('/api/v1/notifications/broadcast', payload);
    clearNotificationCaches();
    return res.data as NotificationBroadcastResponse;
  },
};


